import { defineModule } from "direct-vuex";
import { moduleActionContext } from "@/store";
import { IntegrationListItem } from "@/models/api/administration/integration/integrationListItem";
import {
  getIntegrationList,
  getIntegrationConfigurationList,
  postUpdateIntegrationConfiguration,
  postAddNewIntegration,
  postTestIntegrationConnection,
} from "@/requests/integration";
import CriterionItemRequest from "@/models/api/filters/criterions/CriterionItemRequest";
import { FILTER_NAMES } from "@/static/filterNames";
import _ from "lodash";
import { IntegrationConfigurationListItem } from "@/models/api/administration/integration/integrationConfigurationListItem";
import { DataOptions } from "vuetify";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";

export interface AdministrationIntegrationStateInterface {
  isLoading: boolean;
  isAddDialog: boolean;
  integrationTableCount: number;
  integrationTableData: Array<IntegrationListItem>;
  integrationTableOptions: DataOptions;
  integrationTableFilters: Array<CriterionItemRequest> | null;
  integrationConfigurationList: Array<IntegrationConfigurationListItem> | null;
  newIntegrationConfigurationList: Array<IntegrationConfigurationListItem>;
  selectedIntegration: IntegrationListItem | null;
}

export const AdministrationIntegrationState: AdministrationIntegrationStateInterface = {
  isLoading: false,
  isAddDialog: false,
  integrationTableCount: -1,
  integrationTableData: new Array<IntegrationListItem>(),
  integrationTableOptions: new DefaultDataOptions(),
  integrationTableFilters: null,
  integrationConfigurationList: null,
  newIntegrationConfigurationList: new Array<IntegrationConfigurationListItem>(),
  selectedIntegration: null,
};

const AdministrationIntegrationModule = defineModule({
  state: AdministrationIntegrationState as AdministrationIntegrationStateInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    setIsLoading(state, param: boolean) {
      state.isLoading = param;
    },
    setIsAddDialog(state, param: boolean) {
      state.isAddDialog = param;
    },
    setIntegrationTableCount(state, param: number) {
      state.integrationTableCount = param;
    },
    setIntegrationTableData(state, param: Array<IntegrationListItem>) {
      state.integrationTableData = param;
    },
    setIntegrationTableOptions(state, param: DataOptions) {
      state.integrationTableOptions = param;
    },
    setIntegrationTableFilters(state, param: Array<CriterionItemRequest> | null) {
      state.integrationTableFilters = param;
    },
    setIntegrationConfigurationList(state, param: Array<IntegrationConfigurationListItem> | null) {
      state.integrationConfigurationList = param;
    },
    setNewIntegrationConfigurationList(state, param: Array<IntegrationConfigurationListItem>) {
      state.newIntegrationConfigurationList = param;
    },
    setSelectedIntegration(state, param: IntegrationListItem | null) {
      state.selectedIntegration = param;
    },
  },
  actions: {
    async downloadIntegrationList(context) {
      const { commit, state } = _moduleActionContext(context);
      commit.setIsLoading(true);
      const response = await getIntegrationList({
        count: state.integrationTableOptions.itemsPerPage,
        page: state.integrationTableOptions.page - 1,
        criterions: _.cloneDeep(state.integrationTableFilters),
        filterName: FILTER_NAMES.IntegrationSettingsFilter,
      });
      commit.setIntegrationTableData(response.items);
      commit.setIntegrationTableCount(response.totalCount);
      if (state.integrationTableData[0] !== null) {
        commit.setSelectedIntegration(state.integrationTableData[0]);
      }
      commit.setIsLoading(false);
    },

    async downloadIntegrationConfigurationList(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.selectedIntegration) {
        const response = await getIntegrationConfigurationList(state.selectedIntegration.id);
        commit.setIntegrationConfigurationList(response);
      } else {
        commit.setIntegrationConfigurationList(null);
      }
    },

    async testIntegrationConnection(context) {
      const { state } = _moduleActionContext(context);
      if (state.selectedIntegration) await postTestIntegrationConnection(state.selectedIntegration.id);
    },

    async updateIntegrationConfiguration(context) {
      const { state } = _moduleActionContext(context);
      if (state.integrationConfigurationList !== null && state.selectedIntegration !== null)
        await postUpdateIntegrationConfiguration(state.selectedIntegration.id, state.integrationConfigurationList);
    },

    async addNewIntegration(context, param: { name: string; providerKey: number }) {
      const { state } = _moduleActionContext(context);
      if (state.integrationConfigurationList !== null)
        await postAddNewIntegration(param.name, param.providerKey, state.newIntegrationConfigurationList);
    },
  },
});

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
export default AdministrationIntegrationModule;
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, AdministrationIntegrationModule);
